<template>
  <div id="license-list" class="h-full">
    <detail-content-layout :label="categoryLabel">
      <template #actionArea>
        <Button
          v-if="!hideOnButtonClick"
          type="border"
          colorType="normal"
          :disabled="isAnyCardEditing"
          label="新規作成 / 更新"
          @click="startCreate"
        />
      </template>

      <div class="license-card-stack">
        <!-- 新規作成モード -->
        <template v-if="isCreating">
          <div class="license-card-wrapper">
            <component
              :is="getFormComponent()"
              ref="createForm"
              v-bind="getCreateFormProps()"
            />
            <div class="card-actions">
              <Button
                type="filled"
                colorType="cancel"
                label="キャンセル"
                @click="handleCancelCreate"
              />
              <Button
                type="filled"
                colorType="normal"
                label="保存する"
                @click="handleSaveCreate"
              />
            </div>
          </div>
        </template>

        <!-- 通常（一覧）モード -->
        <template v-else-if="categoryData.licenses.length">
          <div
            v-for="license in categoryData.licenses"
            :key="license.id"
            class="license-card-wrapper"
          >
            <preview-card :last-updated="license.lastUpdated">
              <template #header>
                <preview-card-header
                  :qualification-label="license.name"
                  :is-edit="editingCardId === license.id"
                  :is-disabled="isCardActionsDisabled(license.id)"
                  :on-edit="() => startEditing(license.id)"
                  :on-delete="() => openDeleteModal(license.id, license.name)"
                >
                  <template #chip>
                    <color-chip
                      :label="license.statusReason || ''"
                      :color="statusChipColors[license.statusReason]"
                      :text-color="statusChipTextColors[license.statusReason]"
                  /></template>
                </preview-card-header>
              </template>
              <template #content>
                <!-- 閲覧用 -->
                <component
                  v-if="editingCardId !== license.id"
                  :is="getPreviewComponent()"
                  :viewFields="getViewProps(license)"
                />

                <!-- 編集用 -->
                <div v-else>
                  <component
                    :is="getFormComponent()"
                    ref="editingForm"
                    v-bind="getFormProps(license)"
                  />
                  <div class="card-actions">
                    <Button
                      type="filled"
                      colorType="cancel"
                      label="キャンセル"
                      @click="() => handleCancelEdit()"
                    />
                    <Button
                      type="filled"
                      colorType="normal"
                      label="保存する"
                      @click="() => handleSave(license.id)"
                    />
                  </div>
                </div>
              </template>
            </preview-card>
          </div>
        </template>
        <div v-else class="non-license-message">
          資格・免許の登録がありません
        </div>
      </div>
    </detail-content-layout>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ColorMixin } from "@/mixins";
import DetailContentLayout from "../../components/DetailContentLayout.vue";
import PreviewCard from "../../components/PreviewCard.vue";
import PreviewCardHeader from "../../components/PreviewCardHeader.vue";
import ColorChip from "../../components/ColorChip.vue";
import Button from "../../components/Button.vue";
import CocPreview from "../components/CocPreview.vue";
import OtherPreview from "../components/OtherPreview.vue";
import SeamansBookPreview from "../components/SeamansBookPreview.vue";
import CocForm from "../components/form/CocForm.vue";
import OtherForm from "../components/form/OtherForm.vue";
import SeamansBookForm from "../components/form/SeamansBookForm.vue";
import {
  isCOCCategory,
  isSeamansBookCategory,
  getSubCategory,
} from "../../constants/License";
import LicenseCalculationService from "../../service/LicenseCalculationService";

export default {
  name: "LicenseList",
  mixins: [ColorMixin],
  components: {
    DetailContentLayout,
    PreviewCard,
    PreviewCardHeader,
    ColorChip,
    Button,
    CocPreview,
    OtherPreview,
    SeamansBookPreview,
    CocForm,
    OtherForm,
    SeamansBookForm,
  },

  props: {
    crewId: {
      type: String,
      required: true,
    },
    category: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      statusChipColors: {
        有効: "#039BE5",
        更新済: "#F2F2F2",
        無効: "#F44336",
      },
      statusChipLabels: {
        有効: "有効",
        更新済: "更新済",
        無効: "無効",
      },
      statusChipTextColors: {
        更新済: "#626262",
      },
      isCreating: false,
      editingCardId: null,
      hideOnButtonClick: false,
      isLoadingData: false, // ローディング状態を管理
    };
  },
  computed: {
    ...mapGetters("crewSettingsLicense", [
      "getHavingLicenseListByCategory",
      "getLicenseOptionsByCategory",
    ]),
    /**
     * Getterからカテゴリ別の資格データを取得
     */
    categoryData() {
      return this.getHavingLicenseListByCategory(this.category);
    },
    /** 表示用の資格リスト */
    licenses() {
      return this.categoryData.licenses || [];
    },
    licenseOptions() {
      return this.getLicenseOptionsByCategory(this.category);
    },
    /** 現在のカテゴリのラベルをGetterから取得 */
    categoryLabel() {
      return this.categoryData.categoryLabel;
    },
    /** サブメニュー向けの編集状態 */
    isEditingMode() {
      return Boolean(this.isCreating || this.editingCardId);
    },
    /**
     * 任意のカードが編集中かを判定
     * @returns {boolean} 編集中のカードが存在する場合 true
     */
    isAnyCardEditing() {
      return Boolean(this.editingCardId);
    },
  },
  watch: {
    category: {
      immediate: true,
      async handler() {
        await this.fetchAllData();
      },
    },
    crewId: {
      async handler() {
        await this.fetchAllData();
      },
    },
    isEditingMode: {
      immediate: true,
      handler(value) {
        this.$emit("edit-mode-change", value);
      },
    },
  },
  beforeDestroy() {
    this.$emit("edit-mode-change", false);
  },
  methods: {
    /**
     * 全データを取得（ローディング管理を一元化）
     */
    async fetchAllData() {
      // 既にローディング中の場合はスキップ
      if (this.isLoadingData) {
        return;
      }

      try {
        this.isLoadingData = true;
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });

        await this.fetchLicenseCategories();
        await this.fetchDetailedLicenseData();
      } finally {
        this.$vs.loading.close();
        this.isLoadingData = false;
      }
    },

    /**
     * カテゴリに応じた資格（免許）を取得
     */
    async fetchLicenseCategories() {
      try {
        await this.$store.dispatch("crewSettingsLicense/fetchAllLicenseData", {
          enterpriseCrewId: this.crewId,
        });
      } catch (error) {
        console.error("資格一覧の取得に失敗しました:", error);
        this.$vs.notify({
          title: "資格一覧取得",
          text: "資格一覧を取得できませんでした",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
        this.licenseOptions = [];
      }
    },
    /**
     * カテゴリ別のライセンスIDを取得して、詳細情報を並列で取得
     * crew_having_license_filesを含める
     */
    async fetchDetailedLicenseData() {
      try {
        // 現在のカテゴリのライセンスリストを取得
        const categoryData = this.categoryData || {};
        const licenses = categoryData.licenses || [];

        if (licenses.length === 0) {
          return;
        }

        // 各ライセンスのIDを抽出
        const licenseIds = licenses.map(license => license.id).filter(id => id);

        if (licenseIds.length === 0) {
          return;
        }

        // 並列で詳細情報を取得（crew_having_license_filesを含める）
        await this.$store.dispatch(
          "crewSettingsLicense/fetchHavingLicenseByIds",
          {
            enterpriseCrewId: this.crewId,
            ids: licenseIds,
          }
        );
      } catch (error) {
        console.error("資格詳細情報の取得に失敗しました:", error);
        this.$vs.notify({
          title: "エラー",
          text: "資格詳細情報の取得に失敗しました",
          color: "danger",
        });
      }
    },

    /** 新規作成の保存 */
    async handleSaveCreate() {
      const formRef = this.$refs.createForm;
      if (!formRef) return;

      // フォームのバリデーションを実行
      if (typeof formRef.validate === "function" && !formRef.validate()) {
        this.$vs.notify({
          title: "バリデーションエラー",
          text: "入力内容に誤りがあります。各項目を確認してください。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
        return;
      }

      // フォームコンポーネントから保存用ペイロードを取得
      const payloadData =
        (typeof formRef.getPayload === "function" && formRef.getPayload()) ||
        {};

      // payloadからfilesを抽出
      const files = payloadData.files || [];

      // APIペイロード用に files を除外したデータを構築
      const payload = { ...payloadData };
      delete payload.files;

      // FormDataを生成
      const formData = new FormData();
      formData.append(
        "json",
        new Blob([JSON.stringify(payload)], {
          type: "application/json",
        })
      );
      files.forEach(file => {
        formData.append("files", file);
      });

      // ライセンス名を取得
      const licenseOption = this.licenseOptions.find(
        opt => opt.id === payload.enterprise_crew_license_id
      );
      const licenseName = licenseOption ? licenseOption.label : "";

      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        await this.$store.dispatch("crewSettingsLicense/addHavingLicense", {
          enterpriseCrewId: this.crewId,
          payload: formData,
        });

        this.$vs.notify({
          title: `${this.categoryLabel} 新規追加 / 更新の成功`,
          text: `${licenseName} 新規追加 / 更新に成功しました`,
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });

        this.handleCancelCreate();
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: `${this.categoryLabel} 新規追加 / 更新の失敗`,
          text: `${licenseName} 新規追加 / 更新に失敗しました。時間を空けて再度試みてください。`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }

      // 登録成功後にリストを再取得
      await this.fetchAllData();
    },

    /**
     * 編集内容をデモAPIで保存しローカルストを更新
     * フォームのバリデーション実行、ペイロード構築、API保存を行う
     * @param {string} licenseId - ライセンスID
     * @returns {Promise<void>}
     */
    async handleSave(licenseId) {
      // 編集中のフォーム参照を取得
      const formRef = this.$refs.editingForm.find(Boolean);

      // フォームコンポーネント内で実装しているバリデーションを実行
      if (typeof formRef.validate === "function") {
        const isValid = formRef.validate();
        if (!isValid) {
          this.$vs.notify({
            title: "バリデーションエラー",
            text: "入力内容に誤りがあります。各項目を確認してください。",
            color: "danger",
            iconPack: "feather",
            icon: "icon-alert-circle",
          });
          return;
        }
      }

      // フォームコンポーネントから保存用ペイロードを取得
      const payloadData =
        (typeof formRef.getPayload === "function" && formRef.getPayload()) ||
        {};

      // payloadからfilesを抽出
      const files = payloadData.files || [];

      // APIペイロード用に files を除外したデータを構築
      const payload = { ...payloadData };
      delete payload.files;

      // FormDataを生成
      const formData = new FormData();
      formData.append(
        "json",
        new Blob([JSON.stringify(payload)], {
          type: "application/json",
        })
      );
      files.forEach(file => {
        formData.append("files", file);
      });

      // ライセンス名を取得
      const licenseOption = this.licenseOptions.find(
        opt => opt.id === payload.enterprise_crew_license_id
      );
      const licenseName = licenseOption ? licenseOption.label : "";

      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });

        await this.$store.dispatch("crewSettingsLicense/updateHavingLicense", {
          enterpriseCrewId: this.crewId,
          id: licenseId,
          payload: formData,
        });

        this.editingCardId = null;

        this.$vs.notify({
          title: `${this.categoryLabel} 編集の成功`,
          text: `${licenseName} 編集に成功しました`,
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
      } catch (error) {
        console.error("Failed to save license (demo):", error);

        this.$vs.notify({
          title: `${this.categoryLabel} 編集の失敗`,
          text: `${licenseName} 編集に失敗しました。時間を空けて再度試みてください。`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
        this.hideOnButtonClick = false;
      }

      // 保存成功後にリストを再取得
      await this.fetchAllData();
    },

    /**
     * カテゴリに応じたプレビューコンポーネントを返す
     */
    getPreviewComponent() {
      if (isCOCCategory(this.category)) {
        return CocPreview;
      }
      if (isSeamansBookCategory(this.category)) {
        return SeamansBookPreview;
      }
      return OtherPreview;
    },

    /**
     * カテゴリタイプに応じた編集フォームコンポーネントを返す
     * @param {string} categoryType - 資格カテゴリタイプ
     * @returns {Object} VueコンポーネントObject（CocForm | SeamansBookForm | OtherForm）
     */
    getFormComponent() {
      if (isCOCCategory(this.category)) {
        return CocForm;
      }
      if (isSeamansBookCategory(this.category)) {
        return SeamansBookForm;
      }
      return OtherForm;
    },

    /**
     * フォームコンポーネントに渡すプロップオブジェクトを構築
     * @param {Object} license - ライセンスオブジェクト
     * @returns {Object} フォームコンポーネント用プロップ（license, initialFields, licenseOptions, subCategory）
     */
    getFormProps(license) {
      const props = {
        license: license.enterprise_crew_license_id,
        licenseOptions: this.licenseOptions,
      };

      // COC
      if (isCOCCategory(this.category)) {
        return {
          ...props,
          initialFields: {
            licenseType: license.enterprise_crew_license_id,
            code: license.number,
            issueDate: license.start_date,
            remarks: license.remarks,
            attachments: license.crew_having_license_files.map(file => ({
              id: file.id,
              name: file.name,
              previewUrl: file.url,
            })),
          },
        };
      }
      // Seamans Book
      if (isSeamansBookCategory(this.category)) {
        return {
          ...props,
          initialFields: {
            licenseType: license.enterprise_crew_license_id,
            identifier: license.number,
            issueDate: license.start_date,
            nextUpdateDate: license.expiration_date,
            remarks: license.remarks,
            attachments: license.crew_having_license_files.map(file => ({
              id: file.id,
              name: file.name,
              previewUrl: file.url,
            })),
          },
        };
      }
      // そのほか
      return {
        ...props,
        initialFields: {
          licenseType: license.enterprise_crew_license_id,
          number: license.number,
          issueDate: license.start_date,
          renewalDate: license.expiration_date,
          remarks: license.remarks,
          attachments: license.crew_having_license_files.map(file => ({
            id: file.id,
            name: file.name,
            previewUrl: file.url,
          })),
        },
      };
    },

    /** 新規作成フォーム用のpropsを組み立て（licenseは渡さない） */
    getCreateFormProps() {
      return {
        licenseOptions: this.licenseOptions,
      };
    },

    /** 閲覧用のpropsを組み立て */
    getViewProps(license) {
      if (isCOCCategory(this.category)) {
        const historyRestriction = LicenseCalculationService.getHistoryRestriction(
          getSubCategory(
            this.category,
            license.licenseDefinition.subcategory_no
          ),
          license.number,
          license.licenseDefinition.rank_in_subcategory
        );
        return {
          number: license.number,
          licenseTermYears: license.licenseDefinition.license_term_years,
          issueDate: license.start_date,
          nextUpdateDate: license.expiration_date,
          restrictionContent: historyRestriction.restrictionContent
            ? historyRestriction.restrictionContent.join("\n")
            : "",
          restrictionLiftCondition: historyRestriction.requiredVoyageHistory,
          remarks: license.remarks,
          attachments: license.crew_having_license_files.map(file => file.url),
        };
      }
      if (isSeamansBookCategory(this.category)) {
        return {
          number: license.number,
          licenseTermYears: license.licenseDefinition.license_term_years,
          issueDate: license.start_date,
          nextUpdateDate: license.expiration_date,
          remarks: license.remarks,
          attachments: license.crew_having_license_files.map(file => file.url),
        };
      }
      return {
        number: license.number,
        licenseTermYears: license.licenseDefinition.license_term_years,
        issueDate: license.start_date,
        nextUpdateDate: license.expiration_date,
        remarks: license.remarks,
        attachments: license.crew_having_license_files.map(file => file.url),
      };
    },

    /**
     * ヘッダーボタン（新規作成）クリックで作成モードへ切替
     * 編集中カードがある場合は無視
     */
    startCreate() {
      this.isCreating = true;
      this.hideOnButtonClick = true;
    },

    /** 新規作成のキャンセル */
    handleCancelCreate() {
      this.isCreating = false;
      this.hideOnButtonClick = false;
    },

    /**
     * 指定されたカードのアクション（編集・削除ボタン）を無効にするか判定
     * 他のカードが編集中の場合、そのカード以外は無効化
     * @param {string} licenseId - ライセンスID
     * @returns {boolean} 無効にする場合 true
     */
    isCardActionsDisabled(licenseId) {
      return Boolean(this.editingCardId && this.editingCardId !== licenseId);
    },

    /**
     * 指定されたカードの編集モードを開始
     */
    startEditing(licenseId) {
      this.editingCardId = licenseId;
      this.hideOnButtonClick = true;
    },

    /**
     * 編集をキャンセルしプレビュー表示に戻す
     */
    handleCancelEdit() {
      this.editingCardId = null;
      this.hideOnButtonClick = false;
    },

    /**
     * 削除確認モーダルを開く
     * @param {Object} licenseId - 削除対象の資格ID
     */
    openDeleteModal(licenseId, licenseLabel) {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `${licenseLabel}を削除しますか？`,
        text:
          "削除するとデータが完全に削除され、閲覧できなくなります。よろしいですか？",
        acceptText: "削除",
        cancelText: "キャンセル",
        accept: () => {
          this.confirmDelete(licenseId);
        },
      });
    },

    /**
     * 削除を実行
     */
    async confirmDelete(licenseId) {
      if (!licenseId) {
        return;
      }

      // 削除前にライセンス名を取得
      const license = this.licenses.find(l => l.id === licenseId);
      const licenseOption = this.licenseOptions.find(
        opt => opt.id === license.enterprise_crew_license_id
      );
      const licenseName = licenseOption ? licenseOption.label : "";

      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });

        await this.$store.dispatch("crewSettingsLicense/deleteHavingLicense", {
          enterpriseCrewId: this.crewId,
          id: licenseId,
        });

        this.$vs.notify({
          title: `${this.categoryLabel} 削除の成功`,
          text: `${licenseName} 削除に成功しました`,
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
      } catch (error) {
        console.error("Failed to delete license:", error);

        this.$vs.notify({
          title: `${this.categoryLabel} 削除の失敗`,
          text: `${licenseName} 削除に失敗しました。時間を空けて再度試みてください。`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }

      // 削除後にリストを再取得
      await this.fetchAllData();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

#license-list {
  width: 100%;

  .license-card-stack {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;

    .non-license-message {
      width: 100%;
      height: 600px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: Hiragino Sans;
      font-weight: 400;
      font-size: 14px;
      line-height: 41px;
      letter-spacing: 0%;
      text-align: center;
      vertical-align: middle;
    }
  }

  .license-card-wrapper {
    width: 100%;
    max-width: 1200px;
  }

  .card-actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-top: 24px;
  }
}
</style>
